import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  FormLabel,
  FormGroup,
  IconButton,
  Grid,
  FormHelperText,
  Card,
  CardContent,
  Divider,
  Chip,
  Container,
  useTheme,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import apiService from '../services/api';
import { CreateMemberData, EstateAddress, AvailableEstateAddressesResponse } from '../types';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import PaymentIcon from '@mui/icons-material/Payment';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import DrawIcon from '@mui/icons-material/Draw';
import GavelIcon from '@mui/icons-material/Gavel';
import NotesIcon from '@mui/icons-material/Notes';
import SaveIcon from '@mui/icons-material/Save';

// Membership pricing configuration - updated for new membership structure
const membershipPricing = {
  'Home Owner (H/O)': { monthly: 540, annual: 6480 },
  'Tenant': { monthly: 450, annual: 5400 },
  'Outside Member': {
    'Single': {
      contracts: {
        '12 Months': { monthly: 540, upfront: 5832 },
        '6 Months': { monthly: 594, upfront: 3240 },
        '1 Month': { monthly: 702 },
        '2 Weeks': { amount: 438.75 }
      }
    },
    'Family': {
      contracts: {
        '12 Months': { monthly: 1500, upfront: 16200 },
        '6 Months': { monthly: 1650, upfront: 9000 },
        '1 Month': { monthly: 1950 },
        '2 Weeks': { amount: 1218.75 }
      }
    },
    'Scholars (u18 yrs)': {
      contracts: {
        '12 Months': { monthly: 390, upfront: 4212 },
        '6 Months': { monthly: 429, upfront: 2340 },
        '1 Month': { monthly: 507 },
        '2 Weeks': { amount: 316.88 }
      }
    },
    'Parent and child (u18 yrs)': {
      contracts: {
        '12 Months': { monthly: 850, upfront: 9180 },
        '6 Months': { monthly: 935, upfront: 5100 },
        '1 Month': { monthly: 1105 },
        '2 Weeks': { amount: 690.63 }
      }
    },
    'Pensioner': {
      contracts: {
        '12 Months': { monthly: 390, upfront: 4212 },
        '6 Months': { monthly: 429, upfront: 2340 },
        '1 Month': { monthly: 507 },
        '2 Weeks': { amount: 316.88 }
      }
    },
    'Police': {
      contracts: {
        '12 Months': { monthly: 420, upfront: 4536 },
        '6 Months': { monthly: 462, upfront: 2520 },
        '1 Month': { monthly: 546 },
        '2 Weeks': { amount: 341.25 }
      }
    }
  }
};

interface MemberFormData {
  // Membership Details
  rate: number;
  membershipPosition: 'Home Owner (H/O)' | 'Tenant' | 'Outside Member'; // New top-level field
  membershipType?: 'Single' | 'Family' | 'Scholars (u18 yrs)' | 'Parent and child (u18 yrs)' | 'Pensioner' | 'Police'; // Only for Outside Members
  contractLength?: string; // New field for contract length
  startDate: string; // Contract start date - auto-calculated for Outside Members
  expiryDate: string;
  paymentDay: '25th' | '1st';
  paymentFrequency: 'Monthly' | 'Upfront';
  paymentType: 'Debit Order' | 'EFT/Cash';
  accountReference: string;
  proRata: number;
  joiningFee: number;
  accessFee: number; // Default access fee

  // Member's Particulars
  firstName: string;
  lastName: string;
  address: string;
  phone: {
    mobile: string;
    work: string;
  };
  email: string;
  idNumber: string;
  employer: {
    name: string;
    address: string;
  };
  businessTel: string;
  occupation: string;
  dateOfBirth: string;

  // Emergency Contact (required by server)
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  };

  // Spouse's Particulars
  spouse: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    idNumber: string;
    occupation: string;
  };

  // Children's Particulars
  children: {
    name: string;
    dateOfBirth: string;
    idNumber: string;
  }[];

  // Debit Order Authority
  debitOrder: {
    accountHolder: string;
    address: string;
    contactNumbers: {
      cell: string;
      work: string;
    };
    bank: string;
    branchCode: string;
    accountNumber: string;
    accountType: 'Current' | 'Savings' | 'Transmission';
    amountToDeduct: number;
    debitOrderDate: string;
  };

  // Indemnity & Medical Info
  medicalInfo: {
    allergies: string;
    medication: string;
    recentSurgery: string;
    doctorName: string;
    doctorNumber: string;
    emergencyContact: string;
  };

  // Signatures
  signedAt: string;
  signedDate: string;
  signedMonth: string;
  signature: string;

  // Legal Acknowledgements
  legalAcknowledgements: {
    mandate: boolean;
    cancellation: boolean;
    assignment: boolean;
    popiAct: boolean;
    indemnity: boolean;
  };

  notes: string;
  estateAddressId: string;
}

const AddMember: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Estate Address state
  const [villages, setVillages] = useState<string[]>([]);
  const [availableAddresses, setAvailableAddresses] = useState<EstateAddress[]>([]);
  const [selectedVillage, setSelectedVillage] = useState<string>('');
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const navigate = useNavigate();
  const theme = useTheme();

  // Refs for each section to enable scrolling to errors
  const section1Ref = useRef<HTMLDivElement>(null);
  const section2Ref = useRef<HTMLDivElement>(null);
  const section3aRef = useRef<HTMLDivElement>(null);
  const section3bRef = useRef<HTMLDivElement>(null);
  const section4Ref = useRef<HTMLDivElement>(null);
  const section5Ref = useRef<HTMLDivElement>(null);
  const section6Ref = useRef<HTMLDivElement>(null);
  const section7Ref = useRef<HTMLDivElement>(null);
  const section8Ref = useRef<HTMLDivElement>(null);
  const section9Ref = useRef<HTMLDivElement>(null);

  const { control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<MemberFormData>({
    mode: 'onChange', // Enable real-time validation
    defaultValues: {
      // Membership Details
      rate: 0,
      membershipPosition: 'Outside Member',
      membershipType: 'Single',
      contractLength: '12 Months',
      startDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString().split('T')[0], // Default to first of next month for 12 Months contract
      expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      paymentDay: '1st',
      paymentFrequency: 'Monthly',
      paymentType: 'Debit Order',
      accountReference: '',
      proRata: 0,
      joiningFee: 120,

      // Member's Particulars
      firstName: '',
      lastName: '',
      address: '',
      estateAddressId: '',
      phone: {
        mobile: '',
        work: '',
      },
      email: '',
      idNumber: '',
      employer: {
        name: '',
        address: '',
      },
      businessTel: '',
      occupation: '',
      dateOfBirth: '',

      // Emergency Contact
      emergencyContact: {
        name: '',
        phone: '',
        relationship: 'owner', // Default to 'owner' from enum values
      },

      // Spouse's Particulars
      spouse: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        idNumber: '',
        occupation: '',
      },

      // Children's Particulars
      children: [{ name: '', dateOfBirth: '', idNumber: '' }],

      // Debit Order Authority
      debitOrder: {
        accountHolder: '',
        address: '',
        contactNumbers: {
          cell: '',
          work: '',
        },
        bank: '',
        branchCode: '',
        accountNumber: '',
        accountType: 'Current',
        amountToDeduct: 0,
        debitOrderDate: '',
      },

      // Indemnity & Medical Info
      medicalInfo: {
        allergies: '',
        medication: '',
        recentSurgery: '',
        doctorName: '',
        doctorNumber: '',
        emergencyContact: '',
      },

      // Signatures
      signedAt: '',
      signedDate: new Date().toISOString().split('T')[0],
      signedMonth: '',
      signature: '',

      // Legal Acknowledgements
      legalAcknowledgements: {
        mandate: false,
        cancellation: false,
        assignment: false,
        popiAct: false,
        indemnity: false,
      },

      notes: '',
    },
  });

  const { fields: childrenFields, append: appendChild, remove: removeChild } = useFieldArray({
    control,
    name: 'children',
  });

  // Watch the membership position, membership type, contract length, and payment frequency for auto-rate calculation
  const currentMembershipPosition = watch('membershipPosition');
  const currentMembershipType = watch('membershipType');
  const currentContractLength = watch('contractLength');
  const currentPaymentFrequency = watch('paymentFrequency');
  const currentPaymentType = watch('paymentType');
  const currentRate = watch('rate');
  const currentStartDate = watch('startDate');

  // Function to check if current membership type allows family members
  const allowsFamilyMembers = () => {
    const familyMembershipTypes = ['Home Owner (H/O)', 'Tenant', 'Family', 'Parent and child (u18 yrs)'];
    return familyMembershipTypes.includes(currentMembershipType);
  };

  // Function to extract date of birth from South African ID number
  const extractDateOfBirthFromId = (idNumber: string): string => {
    // Only extract date from SA ID numbers (13 digits)
    if (!idNumber || idNumber.length !== 13 || !/^\d{13}$/.test(idNumber)) {
      return '';
    }

    // Extract YYMMDD from the first 6 digits
    const yearPart = idNumber.substring(0, 2);
    const monthPart = idNumber.substring(2, 4);
    const dayPart = idNumber.substring(4, 6);

    // Convert 2-digit year to 4-digit year
    // If year is 00-25, assume 2000-2025, otherwise assume 1900-1999
    const century = parseInt(yearPart) <= 25 ? 2000 : 1900;
    const fullYear = century + parseInt(yearPart);

    // Validate the date components
    const month = parseInt(monthPart);
    const day = parseInt(dayPart);

    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return '';
    }

    // Create date string in YYYY-MM-DD format
    const dateString = `${fullYear}-${monthPart.padStart(2, '0')}-${dayPart.padStart(2, '0')}`;

    // Validate that the date is valid
    const date = new Date(dateString);
    if (date.getFullYear() !== fullYear || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return '';
    }

    return dateString;
  };

  // Auto-calculate rate when membership position, type, contract length, or payment frequency changes
  useEffect(() => {
    if (currentMembershipPosition) {
      let correctRate = 0;
      let suggestedPaymentFrequency = currentPaymentFrequency;

      // Handle Home Owner and Tenant (legacy pricing)
      if (currentMembershipPosition === 'Home Owner (H/O)' || currentMembershipPosition === 'Tenant') {
        const pricing = membershipPricing[currentMembershipPosition];
        correctRate = currentPaymentFrequency === 'Monthly' ? pricing.monthly : pricing.annual;

        // For Home Owner and Tenant, start date is today
        setValue('startDate', new Date().toISOString().split('T')[0]);
      }
      // Handle Outside Member (contract-based pricing)
      else if (currentMembershipPosition === 'Outside Member' && currentMembershipType && currentContractLength) {
        const outsideMemberPricing = membershipPricing['Outside Member'];
        const typePricing = outsideMemberPricing[currentMembershipType];
        if (typePricing && typePricing.contracts) {
          const contractPricing = typePricing.contracts[currentContractLength as keyof typeof typePricing.contracts];
          if (contractPricing) {
            // Auto-set payment frequency based on contract type
            if (currentContractLength === '2 Weeks') {
              // 2 Weeks only has single amount - set to Monthly as default but will show as read-only
              suggestedPaymentFrequency = 'Monthly';
            } else if (currentContractLength === '1 Month') {
              // 1 Month only has monthly option
              suggestedPaymentFrequency = 'Monthly';
            } else if (!currentPaymentFrequency) {
              // Default to Monthly for contracts that support both
              if ('monthly' in contractPricing) {
                suggestedPaymentFrequency = 'Monthly';
              } else if ('upfront' in contractPricing) {
                suggestedPaymentFrequency = 'Upfront';
              }
            }

            // Update payment frequency if it changed
            if (suggestedPaymentFrequency !== currentPaymentFrequency) {
              setValue('paymentFrequency', suggestedPaymentFrequency);
            }

            // Calculate rate based on selected payment frequency
            if (currentPaymentFrequency === 'Monthly' && 'monthly' in contractPricing) {
              correctRate = contractPricing.monthly;
            } else if (currentPaymentFrequency === 'Upfront' && 'upfront' in contractPricing) {
              correctRate = contractPricing.upfront;
            } else if ('amount' in contractPricing) {
              // For contracts with only amount (like 2 Weeks)
              correctRate = contractPricing.amount;
            } else if ('monthly' in contractPricing) {
              // Fallback to monthly if available
              correctRate = contractPricing.monthly;
            }

            // Auto-calculate start date for Outside Members
            const today = new Date();
            let startDate = new Date(today);

            // If contract length is greater than 1 month AND payment frequency is monthly, start date is first of following month
            // If payment frequency is upfront, start immediately regardless of contract length
            if ((currentContractLength === '12 Months' || currentContractLength === '6 Months') && currentPaymentFrequency === 'Monthly') {
              startDate.setMonth(today.getMonth() + 1);
              startDate.setDate(1);
            } else {
              // For upfront payments or short contracts (1 Month and 2 Weeks), start immediately
              startDate = today;
            }

            setValue('startDate', startDate.toISOString().split('T')[0]);
          }
        }
      } if (correctRate > 0) {
        setValue('rate', correctRate);
      }

      // Auto-calculate expiry date based on membership position and contract length
      if (currentMembershipPosition === 'Home Owner (H/O)' || currentMembershipPosition === 'Tenant') {
        // Set expiry date to 50 years in the future for Home Owner and Tenant
        const startDateValue = watch('startDate');
        const startDate = startDateValue ? new Date(startDateValue) : new Date();
        const expiryDate = new Date(startDate);
        expiryDate.setFullYear(startDate.getFullYear() + 50);
        setValue('expiryDate', expiryDate.toISOString().split('T')[0]);
      } else if (currentMembershipPosition === 'Outside Member' && currentContractLength) {
        const startDateValue = watch('startDate');
        const startDate = startDateValue ? new Date(startDateValue) : new Date();
        let expiryDate = new Date(startDate);

        switch (currentContractLength) {
          case '12 Months':
            expiryDate.setFullYear(startDate.getFullYear() + 1);
            break;
          case '6 Months':
            expiryDate.setMonth(startDate.getMonth() + 6);
            break;
          case '1 Month':
            expiryDate.setMonth(startDate.getMonth() + 1);
            break;
          case '2 Weeks':
            expiryDate.setDate(startDate.getDate() + 14);
            break;
          default:
            expiryDate.setFullYear(startDate.getFullYear() + 1);
        }

        setValue('expiryDate', expiryDate.toISOString().split('T')[0]);
      }
    }
  }, [currentMembershipPosition, currentMembershipType, currentContractLength, currentPaymentFrequency, setValue, watch]);

  // Function to calculate pro rata based on current date, rate, payment frequency, membership position, and start date
  const calculateProRata = (rate: number, paymentFrequency: 'Monthly' | 'Upfront', membershipPosition: string, startDate?: string): number => {
    if (!rate || rate <= 0) return 0;

    // Pro rata calculation only applies to Outside Members with monthly payment frequency
    if (membershipPosition === 'Outside Member' && paymentFrequency === 'Monthly') {
      // Calculate pro rata for the current month (remaining days until start date)
      const today = new Date();
      const contractStartDate = startDate ? new Date(startDate) : today;

      // If start date is in the future (next month), calculate pro rata for current month
      if (contractStartDate > today) {
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();

        // Get the last day of current month
        const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

        // Get current day of month
        const currentDay = today.getDate();

        // Calculate remaining days in the month (including today)
        const remainingDays = lastDayOfMonth - currentDay + 1;

        // Calculate pro rata amount
        const dailyRate = rate / lastDayOfMonth;
        const proRataAmount = dailyRate * remainingDays;

        return Math.round(proRataAmount * 100) / 100; // Round to 2 decimal places
      }
      // If start date is today, no pro rata needed
      else {
        return 0;
      }
    } else {
      // For upfront payments or non-Outside Members, no pro rata calculation
      return 0;
    }
  };

  // Auto-calculate pro rata when rate, payment frequency, membership position, or start date changes
  useEffect(() => {
    if (currentRate && currentRate > 0 && currentPaymentFrequency && currentMembershipPosition) {
      const proRataAmount = calculateProRata(currentRate, currentPaymentFrequency, currentMembershipPosition, currentStartDate);
      console.log(`Pro rata calculation: Rate=${currentRate}, Frequency=${currentPaymentFrequency}, Position=${currentMembershipPosition}, StartDate=${currentStartDate}, Result=${proRataAmount}`);
      setValue('proRata', proRataAmount);
    }
  }, [currentRate, currentPaymentFrequency, currentMembershipPosition, currentStartDate, setValue]);

  // Scroll to top when validation errors occur
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [errors]);

  // Load available estate addresses on component mount
  useEffect(() => {
    const loadEstateAddresses = async () => {
      try {
        setLoadingAddresses(true);
        const response: AvailableEstateAddressesResponse = await apiService.getAvailableEstateAddresses();
        setAvailableAddresses(response?.addresses || []);
        setVillages(response?.villages || []);
      } catch (error) {
        console.error('Error loading estate addresses:', error);
      } finally {
        setLoadingAddresses(false);
      }
    };

    // Load all estate addresses for Home Owners and Tenants (simplified - no village filtering)
    if (currentMembershipPosition === 'Home Owner (H/O)' || currentMembershipPosition === 'Tenant') {
      loadEstateAddresses();
    }
  }, [currentMembershipPosition]);

  // Filter addresses when village selection changes
  useEffect(() => {
    const filterAddressesByVillage = async () => {
      if (!selectedVillage || (currentMembershipPosition !== 'Home Owner (H/O)' && currentMembershipPosition !== 'Tenant')) {
        return;
      }

      try {
        setLoadingAddresses(true);
        const response = await apiService.getEstateAddressesByVillage(selectedVillage, false);
        setAvailableAddresses(response.addresses);
      } catch (error) {
        console.error('Error filtering addresses by village:', error);
      } finally {
        setLoadingAddresses(false);
      }
    };

    if (selectedVillage) {
      filterAddressesByVillage();
    }
  }, [selectedVillage, currentMembershipPosition]);

  const onSubmit = async (data: MemberFormData) => {
    // Scroll to top when form is submitted
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    setLoading(true);
    setError('');

    // Basic validation for required fields only
    // Email validation removed - email can be empty or any format

    if (!data.phone?.mobile?.trim()) {
      setError('Mobile phone number is required.');
      setLoading(false);
      return;
    }

    if (!data.idNumber?.trim()) {
      setError('ID Number or Passport Number is required.');
      setLoading(false);
      return;
    }

    // Validate family members for membership types that don't allow them
    const familyMembershipTypes = ['Home Owner (H/O)', 'Tenant', 'Family', 'Parent and child (u18 yrs)'];
    const membershipTypeAllowsFamily = familyMembershipTypes.includes(data.membershipType);

    if (!membershipTypeAllowsFamily) {
      // Check if spouse has any data (especially ID number)
      const hasSpouseData = data.spouse?.idNumber?.trim();
      // Check if any children have ID numbers
      const hasChildrenData = data.children && data.children.length > 0 && data.children.some(child => child.idNumber?.trim());

      if (hasSpouseData || hasChildrenData) {
        setError(`${data.membershipType} membership cannot contain other members (Spouse and/or Children). Please remove family member data or select a different membership type.`);
        setLoading(false);
        return;
      }
    }

    // Set a frontend timeout as backup
    timeoutRef.current = setTimeout(() => {
      setError('Request timed out. Please try again.');
      setLoading(false);
    }, 35000); // 35 seconds - slightly longer than server timeout

    try {
      // Transform data to match API expectations
      const memberData: CreateMemberData = {
        // Member's Particulars
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        // Use mobile as the primary phone
        phone: data.phone.mobile || '',
        // Store work phone in notes if provided
        notes: data.phone.work ? `Work Phone: ${data.phone.work}\n${data.notes || ''}` : (data.notes || ''),
        dateOfBirth: data.dateOfBirth,
        address: {
          street: data.address,
          city: '', // These fields are now combined in a single address field
          state: '',
          zipCode: '',
          country: 'South Africa',
        },
        idNumber: data.idNumber,
        employer: data.employer,
        businessTel: data.businessTel,
        occupation: data.occupation,

        // Membership Details
        membershipType: data.membershipPosition === 'Outside Member' ? data.membershipType || 'Single' : data.membershipPosition,
        // Only include contractLength for Outside Members
        ...(data.membershipPosition === 'Outside Member' && data.contractLength && { contractLength: data.contractLength }),
        rate: data.rate,
        startDate: data.startDate, // Use the calculated start date from form
        endDate: data.expiryDate, // Map expiryDate to endDate for the server
        paymentDay: data.paymentDay,
        paymentFrequency: data.paymentFrequency,
        accountReference: data.accountReference,
        proRata: data.proRata,
        joiningFee: data.joiningFee,
        membershipStatus: 'active', // Set default status
        paymentStatus: 'current', // Set default payment status

        // Emergency Contact (required by server)
        emergencyContact: {
          name: data.emergencyContact.name,
          phone: data.emergencyContact.phone,
          relationship: data.emergencyContact.relationship
        },

        // Spouse's Particulars
        spouse: data.spouse,

        // Children's Particulars
        children: data.children.filter(child => child.name.trim() !== ''),

        // Debit Order Authority
        debitOrder: data.debitOrder,

        // Indemnity & Medical Info
        medicalInfo: data.medicalInfo,

        // Signatures
        signedAt: data.signedAt,
        signedDate: data.signedDate,
        signedMonth: data.signedMonth,
        signature: data.signature,

        // Legal Acknowledgements
        legalAcknowledgements: data.legalAcknowledgements,
        
        // Estate Address (for Home Owners and Tenants)
        ...(data.estateAddressId && { estateAddressId: data.estateAddressId })
      };

      await apiService.createMember(memberData);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setSuccess(true);

      // Immediate redirect after successful creation
      navigate('/members');

      // Reset form after navigation
      setTimeout(() => {
        reset();
      }, 100);
    } catch (err: any) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      console.error('Error creating member:', err.response?.data);

      // Handle specific error types
      if (err.response?.data?.error === 'DUPLICATE_ID_NUMBER') {
        setError('A member with this ID Number already exists. Please check the ID Number and try again.');
      } else if (err.response?.data?.message?.includes('duplicate key')) {
        setError('This member appears to already exist in the system. Please check the ID Number.');
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to create member');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Complete the membership application form below
          </Typography>

          {/* Progress Indicator */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {[
                { label: 'Membership', icon: <PaymentIcon /> },
                { label: 'Personal Info', icon: <PersonIcon /> },
                { label: 'Family', icon: <FamilyRestroomIcon /> },
                { label: 'Banking', icon: <PaymentIcon /> },
                { label: 'Medical', icon: <MedicalServicesIcon /> },
                { label: 'Legal', icon: <GavelIcon /> }
              ].map((step, index) => (
                <React.Fragment key={step.label}>
                  <Chip
                    icon={step.icon}
                    label={step.label}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '& .MuiChip-icon': { color: 'primary.main' }
                    }}
                  />
                  {index < 5 && (
                    <Box
                      sx={{
                        width: 20,
                        height: 1,
                        bgcolor: 'divider',
                        mx: 0.5
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Alert Messages */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-message': { fontWeight: 500 }
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-message': { fontWeight: 500 }
            }}
          >
            Member created successfully! Redirecting...
          </Alert>
        )}

        {/* Main Form Container */}
        <Paper
          elevation={4}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          {/* Form Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
              p: 3,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Alert
              severity="info"
              sx={{
                backgroundColor: 'transparent',
                border: 'none',
                '& .MuiAlert-icon': { color: 'primary.main' }
              }}
            >
              All required fields must be filled and all legal acknowledgements must be checked to submit the form.
            </Alert>
          </Box>

          <Box sx={{ p: 4 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Section 1: Membership Details */}
              <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }} ref={section1Ref}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: 'primary.main',
                          color: 'white',
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <PaymentIcon />
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          1. Membership Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Configure membership type, rates, and payment terms
                        </Typography>
                      </Box>
                    </Box>
     
                    {currentMembershipPosition === 'Outside Member' && (
                      <Box sx={{ width: 300 }}>
                        <Controller
                          name="accountReference"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Account Reference"
                              fullWidth
                              placeholder="Optional reference number"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                          )}
                        />
                      </Box>
                    )}
                  </Box>
                  {/* Membership Position Selection */}
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={3}>
                    {/* Row 1: Membership Configuration */}
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="membershipPosition"
                        control={control}
                        rules={{ required: 'Membership position is required' }}
                        render={({ field }) => (
                          <FormControl fullWidth error={!!errors.membershipPosition} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                            <InputLabel>Membership</InputLabel>
                            <Select
                              {...field}
                              label="Membership"
                              sx={{ borderRadius: 2 }}
                            >
                              <MenuItem value="Home Owner (H/O)">Home Owner (H/O)</MenuItem>
                              <MenuItem value="Tenant">Tenant</MenuItem>
                              <MenuItem value="Outside Member">Outside Member</MenuItem>
                            </Select>
                            {errors.membershipPosition && <FormHelperText>{errors.membershipPosition.message}</FormHelperText>}
                          </FormControl>
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      {currentMembershipPosition === 'Outside Member' ? (
                        <Controller
                          name="membershipType"
                          control={control}
                          rules={{ required: 'Membership type is required for Outside Members' }}
                          render={({ field }) => (
                            <FormControl fullWidth error={!!errors.membershipType} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                              <InputLabel>Membership Type</InputLabel>
                              <Select
                                {...field}
                                label="Membership Type"
                                sx={{ borderRadius: 2 }}
                              >
                                <MenuItem value="Single">Single</MenuItem>
                                <MenuItem value="Family">Family</MenuItem>
                                <MenuItem value="Scholars (u18 yrs)">Scholars (u18 yrs)</MenuItem>
                                <MenuItem value="Parent and child (u18 yrs)">Parent and child (u18 yrs)</MenuItem>
                                <MenuItem value="Pensioner">Pensioner</MenuItem>
                                <MenuItem value="Police">Police</MenuItem>
                              </Select>
                              {errors.membershipType && <FormHelperText>{errors.membershipType.message}</FormHelperText>}
                            </FormControl>
                          )}
                        />
                      ) : (
                        <Box sx={{ 
                          height: '56px', 
                          display: 'flex', 
                          alignItems: 'center',
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          pl: 2
                        }}>
                          
                        </Box>
                      )}
                    </Grid>

                    <Grid item xs={12} md={4}>
                      {currentMembershipPosition === 'Outside Member' ? (
                        <Controller
                          name="contractLength"
                          control={control}
                          rules={{ required: 'Contract length is required for Outside Members' }}
                          render={({ field }) => (
                            <FormControl fullWidth error={!!errors.contractLength} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                              <InputLabel>Contract Length</InputLabel>
                              <Select
                                {...field}
                                label="Contract Length"
                                sx={{ borderRadius: 2 }}
                              >
                                <MenuItem value="12 Months">12 Months</MenuItem>
                                <MenuItem value="6 Months">6 Months</MenuItem>
                                <MenuItem value="1 Month">1 Month</MenuItem>
                                <MenuItem value="2 Weeks">2 Weeks</MenuItem>
                              </Select>
                              {errors.contractLength && <FormHelperText>{errors.contractLength.message}</FormHelperText>}
                            </FormControl>
                          )}
                        />
                      ) : (
                        <Box sx={{ 
                          height: '56px', 
                          display: 'flex', 
                          alignItems: 'center',
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          pl: 2
                        }}>
                          
                        </Box>
                      )}
                    </Grid>

                    {/* Row 2: Date and Rate Information - Hide start date for Home Owner and Tenant */}
                    {currentMembershipPosition !== 'Home Owner (H/O)' && currentMembershipPosition !== 'Tenant' && (
                      <Grid item xs={12} md={4}>
                        <Controller
                          name="startDate"
                          control={control}
                          rules={{ required: 'Start date is required' }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Contract Start Date"
                              type="date"
                              error={!!errors.startDate}
                              helperText={
                                errors.startDate?.message ||
                                (currentMembershipPosition === 'Outside Member' &&
                                  (currentContractLength === '12 Months' || currentContractLength === '6 Months') &&
                                  currentPaymentFrequency === 'Monthly'
                                  ? 'Auto-set to 1st of next month for monthly payments on contracts >1 month'
                                  : 'Auto-set based on membership type and payment frequency'
                                )
                              }
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: 2 },
                                '& input': { padding: '16.5px 14px' }
                              }}
                            />
                          )}
                        />
                      </Grid>
                    )}

                    {/* Hide contract start and expiry date fields for Home Owner and Tenant */}
                    {currentMembershipPosition !== 'Home Owner (H/O)' && currentMembershipPosition !== 'Tenant' && (
                      <Grid item xs={12} md={4}>
                        <Controller
                          name="expiryDate"
                          control={control}
                          rules={{ required: 'Expiry date is required' }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Membership Expiry Date"
                              type="date"
                              error={!!errors.expiryDate}
                              helperText={errors.expiryDate?.message || 'Auto-calculated based on start date and contract length'}
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: 2 },
                                '& input': { padding: '16.5px 14px' }
                              }}
                            />
                          )}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} md={4}>
                      <Controller
                        name="rate"
                        control={control}
                        rules={{
                          required: 'Rate is required',
                          validate: (value) => value > 0 || 'Rate must be greater than 0'
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label={currentPaymentFrequency === 'Upfront' ? 'Rate (R)' : 'Monthly Rate (R)'}
                            type="number"
                            error={!!errors.rate}
                            fullWidth
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            InputProps={{
                              startAdornment: 'R',
                              readOnly: false,
                            }}
                            helperText={errors.rate?.message}
                          />
                        )}
                      />
                    </Grid>

                    {/* Row 3: Payment Configuration - Hidden for Home Owner and Tenant */}
                    {(currentMembershipPosition !== 'Home Owner (H/O)' && currentMembershipPosition !== 'Tenant') && (
                      <>
                        {currentPaymentType !== 'EFT/Cash' && (
                        <Grid item xs={12} md={4}>
                          <Controller
                            name="paymentDay"
                            control={control}
                            render={({ field }) => (
                              <FormControl fullWidth>
                                <InputLabel>Payment Day</InputLabel>
                                <Select
                                  {...field}
                                  label="Payment Day"
                                  sx={{ borderRadius: 2 }}
                                >
                                  <MenuItem value="1st">1st of Month</MenuItem>
                                  <MenuItem value="25th">25th of Month</MenuItem>
                                </Select>
                              </FormControl>
                            )}
                          />
                        </Grid>
                        )}

                        {currentPaymentType === 'EFT/Cash' && (
                        <Grid item xs={12} md={4}>
                          <Controller
                            name="paymentFrequency"
                            control={control}
                            render={({ field }) => {
                              // Simple payment options for EFT/Cash
                              const getPaymentOptions = () => {
                                return [
                                  { value: 'Monthly', label: 'Monthly' },
                                  { value: 'Upfront', label: 'Upfront' }
                                ];
                              };

                              const paymentOptions = getPaymentOptions();

                              return (
                                <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                  <InputLabel>Payment Option</InputLabel>
                                  <Select
                                    {...field}
                                    label="Payment Option"
                                    sx={{ borderRadius: 2 }}
                                    disabled={paymentOptions.length <= 1}
                                  >
                                    {paymentOptions.map(option => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              );
                            }}
                          />
                        </Grid>
                        )}

                        <Grid item xs={12} md={4}>
                          <Controller
                            name="paymentType"
                            control={control}
                            render={({ field }) => (
                              <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                <InputLabel>Payment Type</InputLabel>
                                <Select
                                  {...field}
                                  label="Payment Type"
                                  sx={{ borderRadius: 2 }}
                                >
                                  <MenuItem value="Debit Order">Debit Order</MenuItem>
                                  <MenuItem value="EFT/Cash">EFT/Cash</MenuItem>
                                </Select>
                              </FormControl>
                            )}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12} md={4}>
                      <Controller
                        name="joiningFee"
                        control={control}
                        rules={{
                          validate: (value) => value >= 0 || 'Joining fee must be 0 or greater'
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Joining Fee (R)"
                            type="number"
                            fullWidth
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            InputProps={{
                              startAdornment: 'R',
                            }}
                            error={!!errors.joiningFee}
                            helperText={errors.joiningFee?.message}
                          />
                        )}
                      />
                    </Grid>

                    {/* Row 4: Pro-Rata and Total Summary */}
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="proRata"
                        control={control}
                        rules={{
                          validate: (value) => value >= 0 || 'Pro-rata must be 0 or greater'
                        }}
                        render={({ field }) => (
                          <Box>
                            <TextField
                              {...field}
                              label="Pro-Rata Cost (R)"
                              type="number"
                              fullWidth
                              value={field.value || ''}
                              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              placeholder={
                                currentMembershipPosition === 'Outside Member' && currentPaymentFrequency === 'Monthly'
                                  ? "Remaining month amount"
                                  : "0"
                              }
                              InputProps={{
                                startAdornment: 'R',
                              }}
                              error={!!errors.proRata}
                              helperText={errors.proRata?.message}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              {currentMembershipPosition === 'Outside Member' && currentPaymentFrequency === 'Monthly'
                                ? `Auto-calculated for remaining days in ${new Date().toLocaleDateString('en-GB', { month: 'long' })}`
                                : currentMembershipPosition === 'Outside Member'
                                  ? 'No pro-rata for upfront payments'
                                  : 'Pro-rata only applies to Outside Members with monthly payments'
                              }
                            </Typography>
                          </Box>
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      {/* Empty space for balance */}
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 2,
                        p: 2,
                        border: '1px dashed',
                        borderColor: 'primary.main'
                      }}>
                        <Box sx={{ textAlign: 'center', width: '100%' }}>
                          <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                            Total Setup Cost (Due Now)
                          </Typography>
                          <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                            R {(
                              (Number(watch('joiningFee')) || 0) +
                              (Number(watch('proRata')) || 0) +
                              (currentPaymentFrequency === 'Upfront' ||
                              currentContractLength === '1 Month' ||
                              currentContractLength === '2 Weeks'
                              ? (Number(watch('rate')) || 0) : 0)
                            ).toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Section 2: Member's Particulars */}
              <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }} ref={section2Ref}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'primary.main',
                        color: 'white',
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <PersonIcon />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        2. Member's Particulars
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Personal information and contact details (SA ID Number or Passport Number required)
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="firstName"
                        control={control}
                        rules={{ required: 'First name is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="First Name"
                            error={!!errors.firstName}
                            helperText={errors.firstName?.message}
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="lastName"
                        control={control}
                        rules={{ required: 'Last name is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Last Name"
                            error={!!errors.lastName}
                            helperText={errors.lastName?.message}
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      {(currentMembershipPosition === 'Home Owner (H/O)' || currentMembershipPosition === 'Tenant') ? (
                        <>
                          {/* Estate Address Selection for Home Owners and Tenants */}
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                <InputLabel>Village Name</InputLabel>
                                <Select
                                  value={selectedVillage}
                                  label="Village Name"
                                  onChange={(e) => {
                                    setSelectedVillage(e.target.value);
                                    setValue('estateAddressId', ''); // Reset address selection when village changes
                                  }}
                                  disabled={loadingAddresses}
                                >
                                  <MenuItem value="">
                                    <em>Select Village Name</em>
                                  </MenuItem>
                                  {villages.map((village) => (
                                    <MenuItem key={village} value={village}>
                                      {village}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {loadingAddresses && (
                                  <FormHelperText>Loading estates...</FormHelperText>
                                )}
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Controller
                                name="estateAddressId"
                                control={control}
                                rules={{ required: 'House number is required for Home Owners and Tenants' }}
                                render={({ field }) => (
                                  <FormControl
                                    fullWidth
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    error={!!errors.estateAddressId}
                                  >
                                    <InputLabel>House Number</InputLabel>
                                    <Select
                                      {...field}
                                      label="House Number"
                                      disabled={!selectedVillage || loadingAddresses}
                                    >
                                      <MenuItem value="">
                                        <em>Select House Number</em>
                                      </MenuItem>
                                      {availableAddresses
                                        .filter(addr => !selectedVillage || addr.villageName === selectedVillage)
                                        .sort((a, b) => a.houseNumber - b.houseNumber)
                                        .map((address) => (
                                          <MenuItem key={address._id} value={address._id}>
                                            {address.houseNumber}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.estateAddressId && (
                                      <FormHelperText>{errors.estateAddressId.message}</FormHelperText>
                                    )}
                                    {!selectedVillage && !loadingAddresses && (
                                      <FormHelperText>Please select a village name first</FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                            </Grid>
                          </Grid>
                        </>
                      ) : (
                        // Regular Address Field for Outside Members
                        <Controller
                          name="address"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Physical Address"
                              multiline
                              rows={2}
                              fullWidth
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                          )}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="phone.mobile"
                        control={control}
                        rules={{ required: 'Mobile number is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Mobile"
                            error={!!errors.phone?.mobile}
                            helperText={errors.phone?.mobile?.message}
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="phone.work"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Work"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Email"
                            type="email"
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="idNumber"
                        control={control}
                        rules={{
                          required: 'ID Number or Passport Number is required',
                          validate: (value) => {
                            if (!value) return 'ID Number or Passport Number is required';

                            // Check if it's a 13-digit SA ID number
                            const isValidSAID = /^\d{13}$/.test(value);

                            // Check if it's a valid passport number (6-9 alphanumeric characters)
                            const isValidPassport = /^[A-Z]{1,2}\d{6,8}$|^[A-Z]\d{7,8}$|^[A-Z]{2}\d{6,7}$|^\d{8,9}$|^[A-Z]\d{6,8}[A-Z]?$/i.test(value);

                            if (!isValidSAID && !isValidPassport) {
                              return 'Enter a valid 13-digit South African ID Number or Passport Number (6-9 alphanumeric characters)';
                            }

                            return true;
                          }
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="ID Number / Passport Number"
                            error={!!errors.idNumber}
                            helperText={errors.idNumber?.message || 'Enter 13-digit SA ID Number (e.g., 8001015009087) or Passport Number (e.g., A1234567)'}
                            fullWidth
                            placeholder="e.g., 8001015009087 or A1234567"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            onChange={(e) => {
                              const idNumber = e.target.value;
                              field.onChange(idNumber);

                              // Auto-populate date of birth if ID number is valid SA ID
                              if (idNumber.length === 13 && /^\d{13}$/.test(idNumber)) {
                                const dateOfBirth = extractDateOfBirthFromId(idNumber);
                                if (dateOfBirth) {
                                  setValue('dateOfBirth', dateOfBirth);
                                }
                              }
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="employer.name"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Employer"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="employer.address"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Business Address"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="businessTel"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Business Tel Number"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="occupation"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Occupation"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="dateOfBirth"
                        control={control}
                        rules={{ required: 'Date of birth is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Date of Birth"
                            type="date"
                            error={!!errors.dateOfBirth}
                            helperText={errors.dateOfBirth?.message}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              '& .MuiOutlinedInput-root': { borderRadius: 2 },
                              '& input': { padding: '16.5px 14px' }
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Section 3a: Emergency Contact */}
              <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }} ref={section3aRef}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'error.main',
                        color: 'white',
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <ContactEmergencyIcon />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'error.main' }}>
                        3a. Emergency Contact
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Contact person in case of emergency
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="emergencyContact.name"
                        control={control}
                        rules={{ required: 'Emergency contact name is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Emergency Contact Name"
                            error={!!errors.emergencyContact?.name}
                            helperText={errors.emergencyContact?.name?.message}
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Controller
                        name="emergencyContact.phone"
                        control={control}
                        rules={{ required: 'Emergency contact phone is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Emergency Contact Phone"
                            error={!!errors.emergencyContact?.phone}
                            helperText={errors.emergencyContact?.phone?.message}
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Controller
                        name="emergencyContact.relationship"
                        control={control}
                        rules={{ required: 'Emergency contact relationship is required' }}
                        render={({ field }) => (
                          <FormControl fullWidth error={!!errors.emergencyContact?.relationship} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                            <InputLabel id="relationship-label">Relationship to Member</InputLabel>
                            <Select
                              {...field}
                              labelId="relationship-label"
                              label="Relationship to Member"
                            >
                              <MenuItem value="owner">Owner</MenuItem>
                              <MenuItem value="spouse">Spouse</MenuItem>
                              <MenuItem value="child">Child</MenuItem>
                            </Select>
                            {errors.emergencyContact?.relationship && (
                              <FormHelperText>{errors.emergencyContact.relationship.message}</FormHelperText>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Section 3b: Spouse's Particulars - Only show for Family, Parent-child, Home Owner, and Tenant */}
              {allowsFamilyMembers() && (
              <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }} ref={section3bRef}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'secondary.main',
                        color: 'white',
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FamilyRestroomIcon />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                        3b. Spouse's Particulars
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Spouse information (if applicable)
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="spouse.firstName"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="First Name"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="spouse.lastName"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Last Name"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="spouse.email"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Email"
                            type="email"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="spouse.phone"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Phone"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="spouse.idNumber"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="ID Number"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="spouse.occupation"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Occupation"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              )}

              {/* Section 4: Children's Particulars - Only show for Family, Parent-child, Home Owner, and Tenant */}
              {allowsFamilyMembers() && (
              <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }} ref={section4Ref}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'info.main',
                        color: 'white',
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FamilyRestroomIcon />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'info.main' }}>
                        4. Children's Particulars
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add children information (if applicable) - Maximum 3 children
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  {childrenFields.map((field, index) => (
                    <Grid container spacing={3} key={field.id} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={4}>
                        <Controller
                          name={`children.${index}.name` as const}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Name"
                              fullWidth
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <Controller
                          name={`children.${index}.dateOfBirth` as const}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Date of Birth"
                              type="date"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: 2 },
                                '& input': { padding: '16.5px 14px' }
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <Controller
                          name={`children.${index}.idNumber` as const}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="ID Number"
                              fullWidth
                              inputProps={{
                                maxLength: 13,
                                pattern: '[0-9]*'
                              }}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              onChange={(e) => {
                                const idNumber = e.target.value;
                                field.onChange(idNumber);

                                // Auto-populate date of birth if ID number is valid
                                if (idNumber.length === 13 && /^\d{13}$/.test(idNumber)) {
                                  const dateOfBirth = extractDateOfBirthFromId(idNumber);
                                  if (dateOfBirth) {
                                    setValue(`children.${index}.dateOfBirth`, dateOfBirth);
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                          color="error"
                          onClick={() => removeChild(index)}
                          disabled={childrenFields.length === 1}
                          sx={{
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}

                  <Box sx={{ mt: 3 }}>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => appendChild({ name: '', dateOfBirth: '', idNumber: '' })}
                      variant="outlined"
                      size="small"
                      disabled={childrenFields.length >= 3}
                      sx={{ borderRadius: 2 }}
                    >
                      Add Child {childrenFields.length >= 3 ? '(Maximum 3)' : `(${childrenFields.length}/3)`}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
              )}

              {/* Section 5: Debit Order Authority - Hidden for Home Owner and Tenant */}
              {(currentMembershipPosition !== 'Home Owner (H/O)' && currentMembershipPosition !== 'Tenant') && currentPaymentType !== 'EFT/Cash' && (
              <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }} ref={section5Ref}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'success.main',
                        color: 'white',
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <PaymentIcon />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
                        5. Debit Order Authority
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Banking details for payment processing
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="debitOrder.accountHolder"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Name of Account Holder"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="debitOrder.address"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Domicile/Execution Address"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="debitOrder.contactNumbers.cell"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Contact Number (Cell)"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="debitOrder.contactNumbers.work"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Contact Number (Work)"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="debitOrder.bank"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Bank"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="debitOrder.branchCode"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Branch Code"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="debitOrder.accountNumber"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Account Number"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="debitOrder.accountType"
                        control={control}
                        render={({ field }) => (
                          <Box>
                            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                              Type of Account
                            </FormLabel>
                            <RadioGroup row {...field}>
                              <FormControlLabel value="Current" control={<Radio />} label="Current" />
                              <FormControlLabel value="Savings" control={<Radio />} label="Savings" />
                              <FormControlLabel value="Transmission" control={<Radio />} label="Transmission" />
                            </RadioGroup>
                          </Box>
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="debitOrder.amountToDeduct"
                        control={control}
                        rules={{
                          validate: (value) => value >= 0 || 'Amount must be 0 or greater'
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Amount to be Deducted"
                            type="number"
                            fullWidth
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            error={!!errors.debitOrder?.amountToDeduct}
                            helperText={errors.debitOrder?.amountToDeduct?.message}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="debitOrder.debitOrderDate"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Debit Order Date"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              '& .MuiOutlinedInput-root': { borderRadius: 2 },
                              '& input': { padding: '16.5px 14px' }
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              )}

              {/* Section 6: Indemnity & Medical Info */}
              <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }} ref={section6Ref}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'warning.main',
                        color: 'white',
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <MedicalServicesIcon />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.main' }}>
                        6. Indemnity & Medical Info
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Medical information and health considerations
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="medicalInfo.allergies"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Allergies"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="medicalInfo.medication"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Medication"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="medicalInfo.recentSurgery"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Recent Surgery"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="medicalInfo.doctorName"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Doctor's Name"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="medicalInfo.doctorNumber"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Doctor's Number"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="medicalInfo.emergencyContact"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Emergency Contact"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Section 7: Signatures */}
              <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }} ref={section7Ref}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'purple',
                        color: 'white',
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <DrawIcon />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'purple' }}>
                        7. Signatures
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Signature and date information
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="signedAt"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Signed At"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Controller
                        name="signedDate"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="On This"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              '& .MuiOutlinedInput-root': { borderRadius: 2 },
                              '& input': { padding: '16.5px 14px' }
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Controller
                        name="signedMonth"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Day Of"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Controller
                        name="signature"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Signature"
                            fullWidth
                            helperText="For now, please type your name as signature. Digital signature functionality will be added in the future."
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Section 8: Legal Acknowledgements */}
              <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }} ref={section8Ref}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'error.dark',
                        color: 'white',
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <GavelIcon />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'error.dark' }}>
                        8. Legal Acknowledgements
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Required legal agreements and acknowledgements
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <FormControl component="fieldset" sx={{ width: '100%' }} error={!!errors.legalAcknowledgements}>
                    <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        All acknowledgements below are required to proceed with the membership application.
                      </Typography>
                    </Alert>

                    <FormGroup sx={{ '& .MuiFormControlLabel-root': { mb: 2 } }}>
                      <Controller
                        name="legalAcknowledgements.mandate"
                        control={control}
                        rules={{ required: 'You must acknowledge the Mandate authorization' }}
                        render={({ field: { onChange, value } }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={value}
                                onChange={onChange}
                                sx={{
                                  '&.Mui-checked': { color: 'success.main' }
                                }}
                              />
                            }
                            label={
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                I/We hereby acknowledge Mandate authorization
                              </Typography>
                            }
                          />
                        )}
                      />

                      <Controller
                        name="legalAcknowledgements.cancellation"
                        control={control}
                        rules={{ required: 'You must acknowledge the cancellation policy' }}
                        render={({ field: { onChange, value } }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={value}
                                onChange={onChange}
                                sx={{
                                  '&.Mui-checked': { color: 'success.main' }
                                }}
                              />
                            }
                            label={
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                I understand the cancellation policy
                              </Typography>
                            }
                          />
                        )}
                      />

                      <Controller
                        name="legalAcknowledgements.assignment"
                        control={control}
                        rules={{ required: 'You must agree to the Assignment terms' }}
                        render={({ field: { onChange, value } }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={value}
                                onChange={onChange}
                                sx={{
                                  '&.Mui-checked': { color: 'success.main' }
                                }}
                              />
                            }
                            label={
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                I agree to the Assignment terms
                              </Typography>
                            }
                          />
                        )}
                      />

                      <Controller
                        name="legalAcknowledgements.popiAct"
                        control={control}
                        rules={{ required: 'You must agree to the POPI Act terms and conditions' }}
                        render={({ field: { onChange, value } }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={value}
                                onChange={onChange}
                                sx={{
                                  '&.Mui-checked': { color: 'success.main' }
                                }}
                              />
                            }
                            label={
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                I agree to the POPI Act terms and conditions
                              </Typography>
                            }
                          />
                        )}
                      />

                      <Controller
                        name="legalAcknowledgements.indemnity"
                        control={control}
                        rules={{ required: 'You must agree to the Indemnity terms' }}
                        render={({ field: { onChange, value } }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={value}
                                onChange={onChange}
                                sx={{
                                  '&.Mui-checked': { color: 'success.main' }
                                }}
                              />
                            }
                            label={
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                I agree to the Indemnity terms
                              </Typography>
                            }
                          />
                        )}
                      />
                    </FormGroup>
                    <FormHelperText>
                      {errors.legalAcknowledgements?.mandate?.message ||
                        errors.legalAcknowledgements?.cancellation?.message ||
                        errors.legalAcknowledgements?.assignment?.message ||
                        errors.legalAcknowledgements?.popiAct?.message ||
                        errors.legalAcknowledgements?.indemnity?.message ||
                        "All acknowledgements are required"}
                    </FormHelperText>
                  </FormControl>
                </CardContent>
              </Card>

              {/* Section 9: Notes */}
              <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }} ref={section9Ref}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'grey.600',
                        color: 'white',
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <NotesIcon />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'grey.700' }}>
                        9. Notes
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Additional information or comments
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Additional Notes"
                        multiline
                        rows={4}
                        fullWidth
                        placeholder="Add any additional information, special requirements, or comments here..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                  />
                </CardContent>
              </Card>

              {/* Submit Button */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  sx={{
                    minWidth: 200,
                    py: 1.5,
                    px: 4,
                    borderRadius: 3,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                    boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                      boxShadow: '0 6px 10px 4px rgba(25, 118, 210, .3)',
                    },
                    '&:disabled': {
                      background: 'grey.400',
                      boxShadow: 'none',
                    }
                  }}
                >
                  {loading ? 'Creating Member...' : 'Create Member'}
                </Button>
              </Box>
            </form>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AddMember;
